import { useCallback } from "react";
import { useDeviceProgress } from "./useDeviceProgress";

export type SavedWord = { text: string; reading?: string; romaji?: string; meaning?: string; savedAt: string };
export type SavedWords = Record<string, SavedWord>;

export function savedWordKey(text: string, reading?: string) { return `${text}\u0000${reading || ""}`; }

// A URL-fragment-safe id for a saved word, built from the same text+reading
// pairing already used as the React `key` on each saved-words list item
// (deliberately not savedWordKey above - that one's null-byte separator is
// fine for an object key, but not for an id/hash a browser has to parse).
// Used to link straight from the saved-word popover to one specific entry
// on /saved-words (#<id>) - the encoding must match exactly on both ends.
export function savedWordFragmentId(text: string, reading?: string) { return encodeURIComponent(`${text}:${reading}`); }

export function useSavedWords() {
    const { data, update, syncStatus } = useDeviceProgress<SavedWords>("savedWords");
    const save = useCallback(async (word: Omit<SavedWord, "savedAt">) => {
        const key = savedWordKey(word.text, word.reading);
        if (data[key]) return true;
        return update({ kind: "setIfMissing", key, value: { ...word, savedAt: new Date().toISOString() } });
    }, [data, update]);
    const remove = useCallback((word: Pick<SavedWord, "text" | "reading">) => update({ kind: "remove", key: savedWordKey(word.text, word.reading) }), [update]);
    const enrich = useCallback((word: SavedWord, details: Pick<SavedWord, "romaji" | "meaning">) =>
        update({ kind: "set", key: savedWordKey(word.text, word.reading), value: { ...word, ...details } }), [update]);
    return { words: Object.values(data).filter((word): word is SavedWord => Boolean(word && typeof word.text === "string")).sort((a, b) => b.savedAt.localeCompare(a.savedAt)), save, remove, enrich, isSaved: (text: string, reading?: string) => Boolean(data[savedWordKey(text, reading)]), syncStatus };
}
